import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useModalLayer } from '../../../lib/modalStack';
import {
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Image as ImageIcon,
  Link2,
  Plus,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import {
  RESOURCE_KIND_LABELS,
  createResource,
  formatBytes,
  groupByCategory,
  toEmbedUrl,
  type ProgramResource,
  type ResourceCollection,
  type ResourceKind,
} from '../../../lib/programResources';
import { RichTextContent, RichTextEditor } from '../../shared/RichTextEditor';
import { dragRowClass, moveItem, useDragReorder } from './dragReorder';

/**
 * Training & Marketing resources manager.
 *
 * The previous UI could only append a file or a plain-text blob and delete it
 * again — no editing, descriptions, categories, ordering, previews, publish
 * control, video or link support. This is the full content-management surface
 * the brief asks for, shared by both collections.
 */

const KIND_ICON: Record<ResourceKind, React.ComponentType<{ className?: string }>> = {
  file: FileText,
  image: ImageIcon,
  video: Video,
  link: Link2,
  article: FileText,
};

interface Props {
  collection: ResourceCollection;
  resources: ProgramResource[];
  onChange: (resources: ProgramResource[]) => void;
  /** Uploads a file and resolves to its public URL. */
  onUpload?: (file: File) => Promise<{ url: string; mimeType?: string; sizeBytes?: number }>;
  disabled?: boolean;
}

export default function ProgramResourcesEditor({
  collection,
  resources,
  onChange,
  onUpload,
  disabled,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const title = collection === 'training' ? 'Training resources' : 'Marketing resources';
  const blurb =
    collection === 'training'
      ? 'Onboarding material for agents, referrers and internal staff working on this program.'
      : 'Brochures, imagery and copy partners can use to promote this program.';

  const commit = (next: ProgramResource[]) =>
    onChange(next.map((resource, index) => ({ ...resource, sortOrder: index })));

  const reorder = (from: number, to: number) => commit(moveItem(resources, from, to));
  const drag = useDragReorder(reorder);

  const update = (id: string, patch: Partial<ProgramResource>) =>
    commit(
      resources.map((resource) =>
        resource.id === id ? { ...resource, ...patch, updatedAt: new Date().toISOString() } : resource
      )
    );

  const remove = (id: string) => commit(resources.filter((resource) => resource.id !== id));

  const addOfKind = (kind: ResourceKind) => {
    if (kind === 'file' || kind === 'image') {
      setReplacingId(null);
      fileInputRef.current?.click();
      return;
    }
    const resource = { ...createResource(collection, kind), sortOrder: resources.length };
    commit([...resources, resource]);
    setExpandedId(resource.id);
  };

  const handleFile = async (file: File) => {
    if (!onUpload) {
      setError('File uploads are not available in this context.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const uploaded = await onUpload(file);
      const kind: ResourceKind = file.type.startsWith('image/') ? 'image' : 'file';

      if (replacingId) {
        update(replacingId, {
          url: uploaded.url,
          fileName: file.name,
          mimeType: uploaded.mimeType || file.type,
          sizeBytes: uploaded.sizeBytes ?? file.size,
          kind,
        });
      } else {
        const resource: ProgramResource = {
          ...createResource(collection, kind),
          title: file.name.replace(/\.[^.]+$/, ''),
          url: uploaded.url,
          fileName: file.name,
          mimeType: uploaded.mimeType || file.type,
          sizeBytes: uploaded.sizeBytes ?? file.size,
          sortOrder: resources.length,
        };
        commit([...resources, resource]);
        setExpandedId(resource.id);
      }
    } catch (err: any) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setReplacingId(null);
    }
  };

  const previewed = useMemo(
    () => resources.find((resource) => resource.id === previewId) || null,
    [resources, previewId]
  );
  const previewZIndex = useModalLayer(!!previewed, () => setPreviewId(null));
  const grouped = useMemo(() => groupByCategory(resources), [resources]);
  const publishedCount = resources.filter((r) => r.published).length;

  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500">{blurb}</p>
          {resources.length > 0 && (
            <p className="mt-1 text-[11px] font-semibold text-slate-400">
              {publishedCount} of {resources.length} published ·{' '}
              {grouped.length} categor{grouped.length === 1 ? 'y' : 'ies'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {(['file', 'video', 'link', 'article'] as ResourceKind[]).map((kind) => {
            const Icon = KIND_ICON[kind];
            return (
              <button
                key={kind}
                type="button"
                disabled={disabled || uploading}
                onClick={() => addOfKind(kind)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-semibold text-slate-700 hover:border-blue-300 hover:text-brand disabled:opacity-50"
              >
                {kind === 'file' ? <Upload className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                {kind === 'file' ? 'Upload' : kind === 'article' ? 'Article' : RESOURCE_KIND_LABELS[kind].split(' ')[0]}
              </button>
            );
          })}
        </div>
      </header>

      {uploading && <p className="animate-pulse text-xs text-slate-500">Uploading…</p>}
      {error && (
        <p className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-auto" aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </p>
      )}

      {resources.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
          <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">No {collection} resources yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Upload a file, embed a video, add a link or write an article.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {resources.map((resource, index) => {
          const itemProps = drag.getItemProps(index);
          const expanded = expandedId === resource.id;
          const Icon = KIND_ICON[resource.kind];
          return (
            <li
              key={resource.id}
              {...itemProps}
              className={dragRowClass(itemProps['data-dragging'], itemProps['data-drag-over'])}
            >
              <div className="flex items-start gap-2 p-3">
                <span className="mt-1.5 cursor-grab text-slate-300 active:cursor-grabbing" aria-hidden="true">
                  <GripVertical className="h-4 w-4" />
                </span>

                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <Icon className="h-4 w-4" />
                </span>

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      value={resource.title}
                      placeholder="Resource title"
                      disabled={disabled}
                      onChange={(event) => update(resource.id, { title: event.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    />
                    <input
                      value={resource.category || ''}
                      placeholder="Category"
                      disabled={disabled}
                      onChange={(event) => update(resource.id, { category: event.target.value })}
                      className="w-full shrink-0 rounded-lg border border-slate-200 px-3 py-2 text-xs sm:w-40"
                    />
                  </div>

                  <input
                    value={resource.description || ''}
                    placeholder="Short description"
                    disabled={disabled}
                    onChange={(event) => update(resource.id, { description: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />

                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">
                      {RESOURCE_KIND_LABELS[resource.kind]}
                    </span>
                    {resource.fileName && (
                      <span className="truncate font-mono text-[10px]">{resource.fileName}</span>
                    )}
                    {resource.sizeBytes ? <span>{formatBytes(resource.sizeBytes)}</span> : null}
                    <span
                      className={`rounded-full px-2 py-0.5 font-semibold ${
                        resource.published
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {resource.published ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {expanded && (
                    <div className="space-y-3 rounded-xl bg-slate-50 p-3">
                      {resource.kind === 'article' ? (
                        <RichTextEditor
                          value={resource.content || ''}
                          onChange={(html) => update(resource.id, { content: html })}
                          placeholder="Write the article…"
                          minHeight={200}
                          disabled={disabled}
                          ariaLabel={`${resource.title || 'Article'} content`}
                        />
                      ) : (
                        <div className="space-y-2">
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            {resource.kind === 'link' ? 'Destination URL' : 'File / media URL'}
                          </label>
                          <div className="flex gap-2">
                            <input
                              value={resource.url || ''}
                              placeholder="https://…"
                              disabled={disabled}
                              onChange={(event) => update(resource.id, { url: event.target.value })}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"
                            />
                            {onUpload && resource.kind !== 'link' && resource.kind !== 'video' && (
                              <button
                                type="button"
                                disabled={disabled || uploading}
                                onClick={() => {
                                  setReplacingId(resource.id);
                                  fileInputRef.current?.click();
                                }}
                                className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              >
                                Replace
                              </button>
                            )}
                          </div>
                          <input
                            value={resource.thumbnailUrl || ''}
                            placeholder="Thumbnail URL (optional)"
                            disabled={disabled}
                            onChange={(event) => update(resource.id, { thumbnailUrl: event.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={disabled || index === 0}
                    onClick={() => reorder(index, index - 1)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={disabled || index === resources.length - 1}
                    onClick={() => reorder(index, index + 1)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label="Preview"
                    title="Preview"
                    onClick={() => setPreviewId(resource.id)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={resource.published ? 'Unpublish' : 'Publish'}
                    title={resource.published ? 'Unpublish' : 'Publish'}
                    disabled={disabled}
                    onClick={() => update(resource.id, { published: !resource.published })}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    {resource.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : resource.id)}
                    aria-expanded={expanded}
                    className="rounded-lg px-2 py-1 text-[11px] font-bold text-brand hover:bg-brand-active"
                  >
                    {expanded ? 'Done' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${resource.title || 'resource'}`}
                    disabled={disabled}
                    onClick={() => remove(resource.id)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {onUpload && (
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
            event.target.value = '';
          }}
        />
      )}

      {/* ─── Preview ────────────────────────────────────────────────────── */}
      {previewed && ReactDOM.createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/60 p-4"
          style={{ zIndex: previewZIndex }}
          role="dialog"
          aria-modal="true"
          aria-label={`Preview of ${previewed.title}`}
          onClick={() => setPreviewId(null)}
        >
          <div
            className="max-h-[86vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h4 className="text-base font-bold text-slate-900">{previewed.title || 'Untitled'}</h4>
                {previewed.description && (
                  <p className="mt-0.5 text-xs text-slate-500">{previewed.description}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {previewed.url && (
                  <a
                    href={previewed.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {previewed.kind === 'link' ? (
                      <ExternalLink className="h-3.5 w-3.5" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Open
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setPreviewId(null)}
                  aria-label="Close preview"
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {previewed.kind === 'article' ? (
              <RichTextContent
                html={previewed.content}
                fallback={<p className="text-sm text-slate-400">This article is empty.</p>}
              />
            ) : previewed.kind === 'image' && previewed.url ? (
              <img src={previewed.url} alt={previewed.title} className="max-h-[60vh] w-full rounded-xl object-contain" />
            ) : previewed.kind === 'video' && toEmbedUrl(previewed.url) ? (
              <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
                <iframe
                  src={toEmbedUrl(previewed.url) as string}
                  title={previewed.title}
                  className="h-full w-full"
                  allowFullScreen
                />
              </div>
            ) : previewed.url && /\.pdf(\?|$)/i.test(previewed.url) ? (
              <iframe src={previewed.url} title={previewed.title} className="h-[60vh] w-full rounded-xl border border-slate-200" />
            ) : (
              <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
                No inline preview available for this resource type. Use “Open” to view it.
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
